/**
 * useSetupStatus - Hook for checking if initial setup is required.
 *
 * Provides a query hook for fetching the setup status from the backend API.
 */

import { useQuery } from '@tanstack/react-query';
import { getSetupStatus } from '@/lib/api';
import type { SetupStatusResponse } from '@/types/api';

/**
 * Query keys for setup-related queries.
 */
export const setupQueryKeys = {
  status: () => ['setup', 'status'] as const,
};

/**
 * Hook for fetching the setup status.
 *
 * This is used to determine if the app needs initial setup (first run),
 * which typically involves creating the first admin user.
 *
 * @returns Query object with:
 * - `data`: SetupStatusResponse | undefined
 * - `isLoading`: Boolean indicating if query is loading
 * - `isError`: Boolean indicating if query failed
 * - `error`: Error object if query failed
 */
export function useSetupStatus() {
  return useQuery<SetupStatusResponse>({
    queryKey: setupQueryKeys.status(),
    queryFn: getSetupStatus,
    // Only fetch once on mount, setup status doesn't change frequently
    staleTime: 30000,
    // Retry a few times in case of transient failures
    retry: 2,
  });
}
